import "./style/app-navigation-item.scss"
import "./style/app-navigation.scss"

import PathNames from "@app/const/PathNames"
import { getDateParts, useSafeLayoutSettings } from "@app/helpers"
import { GODE_KJOP_TOOLTIP_BODY } from "@app/helpers/godeKjopNudge"
import useGodeKjopNudge from "@app/helpers/useGodeKjopNudge"
import { IconResource, IconSize, RootState } from "@app/types"
import { productViewActions } from "@ng-mw/shared-react-components/product-view"
import { searchActions } from "@ng-mw/shared-react-components/search"
import classNames from "classnames"
import React, { RefObject, useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory, useLocation } from "react-router"

// Icons
import plusIcon from "@ng-mw/reol/icons/plus"
import searchIcon from "@ng-mw/reol/icons/search"
import navCartIcon from "@resources/icons/navCart"
import navCartFilledIcon from "@resources/icons/navCartFilled"
import navCartQuantityIcon from "@resources/icons/navCartQuantity"
import navCartQuantityFilledIcon from "@resources/icons/navCartQuantityFilled"
import navHomeIcon from "@resources/icons/navHome"
import navHomeFilledIcon from "@resources/icons/navHomeFilled"
import navSavingsIcon from "@resources/icons/navSavings"
import navSavingsFilledIcon from "@resources/icons/navSavingsFilled"
import navSearchFilledIcon from "@resources/icons/navSearchFilled"

// Components
import isDebugMode from "@app/helpers/isDebugMode"
import AppLink from "@modules/shared/AppLink"
import PositionableTooltip from "@modules/shared/PositionableTooltip"
import AnchoredTooltip from "@modules/shared/AnchoredTooltip"
import Icon from "@ng-mw/reol/components/Icon"
import { LocalStorage } from "@ng-mw/shared-react-components"
import navForkAndKnife from "@resources/icons/navForkAndKnife"

const hasSeenRecipePageTooltipKey: string = "hasSeenRecipePageTooltip"
const hasSeenRecipePageTooltipTruthValue = "something"

interface AppNavigationPlaceholderProps {
    icon: IconResource
    text: string
    iconSize?: IconSize
}

const AppNavigationPlaceholder = ({
    icon,
    iconSize = { height: "26px", width: "26px" },
    text,
}: AppNavigationPlaceholderProps) => {
    const { safeFooterPaddingBottom } = useSafeLayoutSettings()

    return (
        <li
            className="app-navigation-item"
            style={{ opacity: 0.5 }}
        >
            <AppLink className="app-navigation-item__link">
                <div className="app-navigation-item__icon-wrapper">
                    <Icon
                        icon={icon}
                        className="app-navigation-item__icon"
                        size={iconSize}
                    />
                </div>
                <span
                    className="app-navigation-item__text"
                    style={{ paddingBottom: `${safeFooterPaddingBottom}px` }}
                >
                    {text}
                </span>
            </AppLink>
        </li>
    )
}

interface AppNavigationItemProps {
    to: string
    icon: IconResource
    text: string
    badge?: string | number
    isActive?: boolean
    iconSize?: IconSize
    onClick?: () => void
    onTouchStart?: () => void
    onTouchEnd?: (e: any) => void
    usePlaceholder?: boolean
    liRef?: RefObject<HTMLLIElement | null>
}

const AppNavigationItem = ({
    to,
    icon,
    iconSize = { height: "26px", width: "26px" },
    text,
    isActive: active,
    badge,
    onClick,
    onTouchStart,
    onTouchEnd,
    usePlaceholder = false,
    liRef,
}: AppNavigationItemProps) => {
    const { safeFooterPaddingBottom } = useSafeLayoutSettings()

    return usePlaceholder ? (
        <AppNavigationPlaceholder
            icon={icon}
            text={text}
            iconSize={iconSize}
        />
    ) : (
        <li
            ref={liRef}
            className={classNames("app-navigation-item", {
                "app-navigation-item--active": active,
            })}
        >
            <AppLink
                className="app-navigation-item__link"
                instant
                to={to}
                onClick={onClick}
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
            >
                <div className="app-navigation-item__icon-wrapper">
                    <Icon
                        icon={icon}
                        className={classNames("app-navigation-item__icon", {
                            "app-navigation-item__icon--active": active,
                        })}
                        size={iconSize}
                    />
                    {badge ? <span className="app-navigation-item__badge">{badge}</span> : null}
                </div>
                <span
                    className="app-navigation-item__text"
                    style={{ paddingBottom: `${safeFooterPaddingBottom}px` }}
                >
                    {text}
                </span>
            </AppLink>
        </li>
    )
}

const AppNavigation = () => {
    const dispatch = useDispatch<any>()
    const location = useLocation()
    const newMenuItem = useRef<HTMLLIElement>(null)
    const godeKjopItem = useRef<HTMLLIElement>(null)
    const previousPath = useRef(location.pathname)
    const { safeFooterHeight } = useSafeLayoutSettings()
    const history = useHistory()
    const productView = useSelector((state: RootState) => state.productView)
    const totalCartQuantity = useSelector((state: RootState) => state.cart.totals.totalQuantity)
    const path = location.pathname
    const time = useRef(0)
    const { month } = getDateParts(new Date())
    const isDebugModeActive = isDebugMode()
    const isBestBuyActive = path.startsWith("/bestbuy")
    const isPride = (month > 5 && month < 8) || (isDebugModeActive && month === 5)
    const footerHeight = safeFooterHeight + (isPride ? 8 : 0)

    const waitingForToken = useSelector((state: RootState) => state.app.waitingForToken)
    const { godeKjopNudgeState, dismissTooltip } = useGodeKjopNudge()

    const handleTouchStart = () => {
        time.current = new Date().getTime()
    }

    const handleTouchEnd = (e: React.TouchEvent) => {
        const timeDiff = new Date().getTime() - time.current
        if (timeDiff > 4500) {
            history.push("/debug")
            e.preventDefault()
        }
    }

    const resetProductsSearch = () => {
        dispatch(searchActions.clearFilter())
        dispatch(searchActions.setQuery(null))

        if (productView.popular) {
            dispatch(productViewActions.clearFilter("popular"))
        }
    }

    const getCartIcon = () => {
        if (path === PathNames.Cart) {
            return totalCartQuantity ? navCartQuantityFilledIcon : navCartFilledIcon
        } else {
            return totalCartQuantity ? navCartQuantityIcon : navCartIcon
        }
    }

    const showGodeKjopTooltip = !waitingForToken && godeKjopNudgeState.isTooltipVisible

    useEffect(() => {
        if (!showGodeKjopTooltip || previousPath.current === path) {
            return
        }

        previousPath.current = path
        dismissTooltip()
    }, [dismissTooltip, path, showGodeKjopTooltip])

    return (
        <>
            <nav
                className={classNames("app-navigation", {
                    "app-navigation__pride-banner": isPride,
                })}
                role="navigation"
                style={{ height: `${footerHeight}px` }}
            >
                <ul className="app-navigation__menu">
                    <AppNavigationItem
                        to={PathNames.Home}
                        icon={path === PathNames.Home ? navHomeFilledIcon : navHomeIcon}
                        text="Hjem"
                        isActive={path === PathNames.Home}
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                    />

                    <AppNavigationItem
                        to={PathNames.Search}
                        icon={
                            path === PathNames.Search || path.includes(PathNames.Products)
                                ? navSearchFilledIcon
                                : searchIcon
                        }
                        text="Varer"
                        isActive={path === PathNames.Search || path.includes(PathNames.Products)}
                        onClick={resetProductsSearch}
                    />
                    <AppNavigationItem
                        to={PathNames.Cart}
                        icon={getCartIcon()}
                        text="Handlevogn"
                        isActive={path === PathNames.Cart}
                        badge={totalCartQuantity}
                        usePlaceholder={waitingForToken}
                    />
                    <AppNavigationItem
                        to={PathNames.BestBuy}
                        icon={isBestBuyActive ? navSavingsFilledIcon : navSavingsIcon}
                        text="Gode kjøp"
                        liRef={godeKjopItem}
                        isActive={isBestBuyActive}
                        usePlaceholder={waitingForToken}
                    />
                    <AppNavigationItem
                        to={PathNames.RecipesHome}
                        liRef={newMenuItem}
                        icon={navForkAndKnife}
                        text="Oppskrifter"
                        isActive={path.includes("/recipes")}
                    />
                </ul>
            </nav>
            {!waitingForToken ? (
                <PositionableTooltip
                    anchorRef={newMenuItem}
                    initiallyVisible={
                        LocalStorage.get(hasSeenRecipePageTooltipKey) !==
                        hasSeenRecipePageTooltipTruthValue
                    }
                    onClose={() => {
                        LocalStorage.set(
                            hasSeenRecipePageTooltipKey,
                            hasSeenRecipePageTooltipTruthValue,
                        )
                    }}
                    position="right"
                    isInverted
                    isCloseButtonVisible
                    leftCorrection={-2}
                    topCorrection={-30}
                    body="Du finner nå alle oppskrifter i appen. Søk etter din favoritt!"
                />
            ) : null}
            {showGodeKjopTooltip ? (
                <AnchoredTooltip
                    anchorRef={godeKjopItem}
                    onClose={dismissTooltip}
                    position="right"
                    isDismissedOnAnchorClick
                    isCloseButtonVisible
                    isDismissedOnOutsideClick={false}
                    body={GODE_KJOP_TOOLTIP_BODY}
                />
            ) : null}

            {isDebugModeActive ? (
                <AppLink
                    className="app-navigation__debug-link"
                    instant
                    to={PathNames.Debug}
                >
                    <Icon icon={plusIcon} />
                    <span className="ws-visually-hidden">Debug</span>
                </AppLink>
            ) : null}
        </>
    )
}

export default AppNavigation
